import type { FC } from 'react'
import React, { memo, useMemo, useState, useEffect } from 'react'
import { Line } from '@nivo/line'
import AutoSizer from 'react-virtualized-auto-sizer'
import { isEngageUser, useGetReviewsRatingHistoryQuery } from '@homeviews/store'
import { Spinner } from '@homeviews/shared-ui'
import NoData from '../NoData'
import { transformReviewsRatingsHistoryData } from '../../helpers/transform'
import ChartLegend from '../ChartLegend'
import { formatRating, getChartTooltipDate, isEqual } from '@homeviews/utils'
import { chartTheme } from '../../helpers/layout'
import ChartTooltip from '../ChartTooltip'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useSelector } from 'react-redux'

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    tooltipWrapper: {
      maxWidth: 150,
      width: 150,
      '& p': {
        whiteSpace: 'initial!important',
      },
    },
  }),
)

interface IProps {
  activeFilters: any
  graphDisabled?: boolean
  gridRef?: HTMLElement
}

const RatingsHistoryChart: FC<IProps> = memo(
  ({ activeFilters, graphDisabled, gridRef }) => {
    const classes = useStyles()
    const isEngagePackage = Boolean(useSelector(isEngageUser))

    const { data: reviewsRatingHistoryData, isFetching } =
      useGetReviewsRatingHistoryQuery(
        {
          ...activeFilters,
          engage: isEngagePackage,
        },
        {
          skip: graphDisabled,
        },
      )

    const data = useMemo(
      () => transformReviewsRatingsHistoryData(reviewsRatingHistoryData?.data),
      [reviewsRatingHistoryData?.data],
    )

    const [disabledDatasets, setDisableDatasets] = useState<string[]>([])
    const [filteredData, setFilteredData] = useState<any>(data)

    useEffect(() => {
      if (!filteredData) {
        return
      }

      let newFilteredData = [...data]

      newFilteredData = newFilteredData.filter((item: any) => {
        if (
          disabledDatasets.find(
            (filterValue: string) => filterValue === item?.id,
          )
        ) {
          return false
        }
        return true
      })

      setFilteredData(newFilteredData)
    }, [disabledDatasets, data])

    const handleChartLegendClick = (id: string) => {
      let newDatasets = [...disabledDatasets]

      if (newDatasets.find((itemId: any) => itemId === id)) {
        newDatasets = newDatasets.filter((itemId: any) => itemId !== id)
      } else {
        newDatasets.push(id)
      }
      setDisableDatasets(newDatasets)
    }

    if (!data || !data?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
      }
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    if (isFetching) {
      return <Spinner centered />
    }

    if (!data || !data?.length) {
      return <NoData />
    }

    return (
      <div className={clsx(classes.root, 'l-chartContainer')}>
        <div className="l-chartWrapper">
          <AutoSizer>
            {({ height, width }) => {
              return (
                <Line
                  data={filteredData}
                  enableGridX={false}
                  theme={chartTheme}
                  height={height}
                  width={width}
                  xScale={{
                    type: 'time',
                    format: '%Y-%m-%d',
                  }}
                  colors={{ datum: 'color' }}
                  xFormat="time:%Y-%m-%d"
                  yScale={{
                    type: 'linear',
                    stacked: false,
                  }}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: 0,
                    legendOffset: 12,
                    tickValues: [0, 1, 2, 3, 4, 5],
                  }}
                  axisBottom={{
                    format: '%d/%m/%y',
                    legendOffset: -12,
                    tickSize: 0,
                    tickPadding: 10,
                    tickValues: Math.floor(width / 90),
                  }}
                  pointColor={'white'}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  margin={{ top: 50, right: 30, bottom: 70, left: 40 }}
                  pointSize={7}
                  useMesh={true}
                  tooltip={(tooltip: any) => {
                    let pointIndexText = ''

                    try {
                      const pointIndex: any = tooltip?.point?.data?.rating_count
                      if (pointIndex) {
                        pointIndexText = `(based on ${pointIndex} ${
                          pointIndex === 1 ? ' review' : ' reviews'
                        })`
                      }
                    } catch {
                      //
                    }
                    return (
                      <div className={classes.tooltipWrapper}>
                        <ChartTooltip>
                          <ChartLegend
                            direction="vertical"
                            data={[
                              {
                                text: `${getChartTooltipDate(
                                  tooltip.point?.data?.x,
                                )}: ${formatRating(
                                  tooltip.point?.data?.yFormatted,
                                )} ${pointIndexText}`,
                                shapeColor: tooltip?.point?.serieColor,
                              },
                            ]}
                          />
                        </ChartTooltip>
                      </div>
                    )
                  }}
                />
              )
            }}
          </AutoSizer>
        </div>
        <ChartLegend
          data={data.map((item) => ({
            id: item.id,
            text: item.id,
            shapeColor: item.color,
            disabled: !!disabledDatasets.find(
              (datasetItem: any) => datasetItem === item.id,
            ),
          }))}
          disabledDatasets={disabledDatasets}
          onClick={handleChartLegendClick}
        />
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default RatingsHistoryChart
